import { useTranslations } from '@vocab/react';
import {
  Box,
  HiddenVisually,
  Inline,
  Stack,
  Textarea,
} from 'braid-design-system';
import type React from 'react';
import type { ReactNode } from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import type { StatefulRecommendedQuestion } from '../../App/state';
import CustomQuestionAnswerList from '../CustomQuestionAnswerList/CustomQuestionAnswerList';
import { DeleteCustomQuestion } from '../DeleteCustomQuestion/DeleteCustomQuestion';
import { MultipleChoiceOptions } from '../MultipleChoiceOptions/MultipleChoiceOptions';

export type DraftCustomQuestion = Pick<
  StatefulRecommendedQuestion,
  'text' | 'source'
> & {
  position: number;
  order: number;
  error?: string | null;
  options: string[];
  type: CustomQuestionType;
};

export enum CustomQuestionType {
  FreeText = 'free text',
  YesNo = 'yes or no',
  MultipleChoice = 'multiple choice',
}

interface Props {
  question: DraftCustomQuestion;
  validationFailures: string[];
  order: number;
  id: string;
  isMobile: boolean | null;
  onDelete: () => void;
  onChange: (question: Partial<DraftCustomQuestion>) => void;
}

const generateError = (
  errorMessage: string | undefined | null,
  characterLimitRemaining: number,
  characterLimit: number,
): string | null => {
  if (errorMessage) {
    return errorMessage;
  }

  return characterLimitRemaining < 1
    ? `Your question needs to be a maximum of ${characterLimit} characters`
    : null;
};

export const DraftCustomQuestion: React.FC<Props> = ({
  question,
  order,
  id,
  isMobile,
  onDelete,
  onChange,
}) => {
  const { t } = useTranslations(translations);

  const characterLimit = 230;
  const characterLimitRemaining = Math.max(
    characterLimit - question.text.length,
    0,
  );
  const message = generateError(
    question.error,
    characterLimitRemaining,
    characterLimit,
  );

  return (
    <Box id={String(question.position)} data-testid="question-container">
      <div data-testid={`question ${order}`}>
        <Stack space="medium">
          <CustomQuestionAnswerList question={question} onChange={onChange} />
          <Textarea
            id={id}
            onChange={(e) => {
              onChange({
                text: e.currentTarget.value,
                error: null,
              });
            }}
            value={question.text}
            placeholder={t('custom.question.placeholder')}
            label={t('custom.question.label', {
              HiddenVisually: (children: ReactNode) => (
                <HiddenVisually>{children}</HiddenVisually>
              ),
              order: order.toString(),
            })}
            characterLimit={characterLimit}
            message={message}
            tone={message ? 'critical' : 'neutral'}
            grow={true}
          />
          {question.type === CustomQuestionType.MultipleChoice ? (
            <MultipleChoiceOptions
              options={question.options}
              onChange={(newQuestion) => {
                onChange(newQuestion);
              }}
            />
          ) : null}
          {isMobile ? (
            <DeleteCustomQuestion order={order} onDelete={onDelete} />
          ) : (
            <Inline space="small">
              <DeleteCustomQuestion order={order} onDelete={onDelete} />
            </Inline>
          )}
        </Stack>
      </div>
    </Box>
  );
};

export function isDraftCustomQuestion(
  question: StatefulRecommendedQuestion | DraftCustomQuestion,
): question is DraftCustomQuestion {
  return (question as DraftCustomQuestion).position !== undefined;
}
